import { Box, Typography } from "@mui/material";
import { formatMoney } from "../../../utils/format.util";
import { Quote } from "../../../models/quote.model";
import quoteService from "../../../services/quote.service";
import { useMemo, useState, useEffect } from "react";
import { getStates } from "../../../services/states.service";
import { State } from "../../../models/state.model";

interface QuotePrintTemplateProps {
  quoteData: Partial<Quote>;
}

export default function QuotePrintTemplate({
  quoteData,
}: QuotePrintTemplateProps) {
  const [states, setStates] = useState<State[]>([]);

  useEffect(() => {
    const loadStates = async () => {
      try {
        const statesData = await getStates();
        setStates(statesData);
      } catch (error) {
        console.error("Error loading states:", error);
      }
    };
    loadStates();
  }, []);

  const formatCurrency = (value: string | undefined): string => {
    if (!value) return "$0.00";
    const num = parseFloat(value);
    return isNaN(num) ? "$0.00" : formatMoney(num);
  };

  const formatValue = (value: string | boolean | undefined): string => {
    if (value === undefined || value === null) return "";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return String(value);
  };

  // Calculate fees using quote service if we have the necessary data
  const calculatedFees = useMemo(() => {
    if (!quoteData.titleState || !quoteData.county || !quoteData.basePrice) {
      return null;
    }

    try {
      const basePrice = parseFloat(quoteData.basePrice || "0") || 0;
      const tradeInValue = parseFloat(quoteData.tradeInValue || "0") || 0;
      const purchasePrice = basePrice;

      const feeStructure = quoteService.determineFeeStructure(
        quoteData.titleState,
        quoteData.county
      );

      if (feeStructure.type === "none" || !feeStructure.fees) {
        return null;
      }

      const fees = feeStructure.fees;
      const taxableAmount = purchasePrice - tradeInValue;
      const salesTax = taxableAmount * fees.salesTax;

      return {
        titleFee: fees.titleFee,
        registrationFee: fees.registrationFee,
        salesTax,
        otherFees: fees.otherFees,
        feeStructure,
      };
    } catch (error) {
      console.error("Error calculating fees:", error);
      return null;
    }
  }, [
    quoteData.titleState,
    quoteData.county,
    quoteData.basePrice,
    quoteData.tradeInValue,
    states,
  ]);

  return (
    <Box
      sx={{
        display: "none",
        "@media print": {
          display: "block",
          backgroundColor: "#fff",
          color: "#000",
          padding: "50px",
          fontFamily: "Helvetica, Arial, sans-serif",
          fontSize: "13px",
          lineHeight: "15px",
          pageBreakAfter: "always",
        },
      }}
      className="quote-print-template"
    >
      <Box sx={{ mb: 3 }}>
        <img
          alt="CCL Logo"
          src="/assets/images/ccl-logo.png"
          style={{ width: "100px", marginBottom: "15px" }}
        />
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Date:</strong> {quoteData.date}
          {quoteData.id && (
            <>
              {" | "}
              <strong>Quote ID:</strong> {quoteData.id}
            </>
          )}
          {" | "}
          <strong>Status:</strong> {quoteData.status}
        </Typography>
      </Box>

      {/* Fee Summary Section - ATC Format */}
      <Box sx={{ mb: 3, mt: 4 }}>
        <Typography
          variant="h6"
          sx={{
            mb: 2,
            borderBottom: "2px solid #000",
            pb: 1,
            fontWeight: "bold",
          }}
        >
          Fee Summary
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {(() => {
            const getNumericValue = (value: string | undefined): number => {
              if (!value) return 0;
              const num = parseFloat(value);
              return isNaN(num) ? 0 : num;
            };

            // Calculate taxable amount - include all fees that are part of taxable base
            const basePrice = getNumericValue(quoteData.basePrice);
            const tradeInValue = getNumericValue(quoteData.tradeInValue);
            const docFees = getNumericValue(quoteData.docFees);
            const dealerServiceFee = getNumericValue(
              quoteData.dealerServiceFee
            );
            const addOns = getNumericValue(quoteData.addOns);
            const warrants = getNumericValue(quoteData.warrants);
            const extendedWarranties = getNumericValue(
              quoteData.extendedWarranties
            );
            const protectionPackages = getNumericValue(
              quoteData.protectionPackages
            );
            const theftProtection = getNumericValue(quoteData.theftProtection);
            const insurancePackages = getNumericValue(
              quoteData.insurancePackages
            );
            const taxCreditAmount =
              quoteData.evTaxCredit === "Apply credit to taxable amount"
                ? getNumericValue(quoteData.taxCreditAmount)
                : 0;

            // Taxable value formula: (Base Price + Doc Fees + Additions + Dealer Service Fee + Warranties + Extended Warranties + Protection Packages + Theft Protection + Insurance Packages - Trade In Amount - Tax Credit)
            const taxableAmount =
              basePrice +
              docFees +
              addOns +
              dealerServiceFee +
              warrants +
              extendedWarranties +
              protectionPackages +
              theftProtection +
              insurancePackages -
              tradeInValue -
              taxCreditAmount;

            // Format values for formula display (always show 2 decimals, or 1 for trade-in to match ATC format)
            const formatFormulaValue = (
              val: number,
              decimals: number = 2
            ): string => {
              return val.toFixed(decimals);
            };

            // Use calculated fees if available
            const titleFee = calculatedFees?.titleFee || 0;
            const registrationFee = calculatedFees?.registrationFee || 0;
            // const salesTaxRate = calculatedFees?.feeStructure?.fees?.salesTax || 0;
            const otherFees = calculatedFees?.otherFees || 0;

            // Calculate state and county taxes using actual state and county fees
            // Use the state and county from the fee structure (which comes from titleState and county fields)
            const feeState =
              calculatedFees?.feeStructure?.state || quoteData.titleState;
            const feeCounty =
              calculatedFees?.feeStructure?.county || quoteData.county;

            // Find state and county data to get their tax fees
            const stateData = states.find((s) => s.abbreviation === feeState);
            const countyData = stateData?.counties?.find(
              (c) => c.name === feeCounty
            );

            // Find state tax fee - look for fees that are percentage-based and could be state tax
            // Try multiple patterns: "state tax", "state", "sales tax", or first percentage fee if only one exists
            const stateTaxFee =
              stateData?.fees?.find((f) => {
                const name = f.name.toLowerCase();
                return (
                  (name.includes("state") && name.includes("tax")) ||
                  name === "state tax" ||
                  name === "state sales tax" ||
                  (name.includes("state") && f.type === "percentage")
                );
              }) ||
              (stateData?.fees?.filter((f) => f.type === "percentage")
                .length === 1
                ? stateData.fees.find((f) => f.type === "percentage")
                : null);

            // Find county tax fee - look for fees that are percentage-based and could be county tax
            // Try multiple patterns: "county tax", "county", "sales tax", or first percentage fee if only one exists
            const countyTaxFee =
              countyData?.fees?.find((f) => {
                const name = f.name.toLowerCase();
                return (
                  (name.includes("county") && name.includes("tax")) ||
                  name === "county tax" ||
                  name === "county sales tax" ||
                  (name.includes("county") && f.type === "percentage")
                );
              }) ||
              (countyData?.fees?.filter((f) => f.type === "percentage")
                .length === 1
                ? countyData.fees.find((f) => f.type === "percentage")
                : null);

            // Calculate state tax - can be percentage or fixed
            let stateTax = 0;
            let stateTaxRate = 0;
            if (stateTaxFee) {
              const cost = parseFloat(stateTaxFee.cost) || 0;
              if (stateTaxFee.type === "percentage") {
                stateTaxRate = cost / 100; // Convert percentage to decimal (e.g., 47% becomes 0.47)
                stateTax = taxableAmount * stateTaxRate;
              } else if (stateTaxFee.type === "fixed") {
                stateTax = cost;
                stateTaxRate = 0; // No rate for fixed amounts
              }
            }

            // Calculate county tax - can be percentage or fixed
            let countyTax = 0;
            let countyTaxRate = 0;
            if (countyTaxFee) {
              const cost = parseFloat(countyTaxFee.cost) || 0;
              if (countyTaxFee.type === "percentage") {
                countyTaxRate = cost / 100; // Convert percentage to decimal (e.g., 47% becomes 0.47)
                countyTax = taxableAmount * countyTaxRate;
              } else if (countyTaxFee.type === "fixed") {
                countyTax = cost;
                countyTaxRate = 0; // No rate for fixed amounts
              }
            }

            // Debug: Log if fees are found
            if (feeState && !stateTaxFee) {
              console.log(
                "State Tax fee not found for:",
                feeState,
                "Available fees:",
                stateData?.fees
              );
            }
            if (feeCounty && !countyTaxFee) {
              console.log(
                "County Tax fee not found for:",
                feeCounty,
                "Available fees:",
                countyData?.fees
              );
            }

            // If we have taxesPaidDealerState, use that as state tax (manual override)
            const manualStateTax = getNumericValue(
              quoteData.taxesPaidDealerState
            );
            const finalStateTax =
              manualStateTax > 0 ? manualStateTax : stateTax;
            const finalCountyTax = countyTax;
            const finalSalesTaxSubTotal = finalStateTax + finalCountyTax;

            // Fixed fees from quote data
            const localHandlingFee = docFees || 0;
            const dealerServiceFeeAmount = dealerServiceFee || 0;
            const deliveryFees = getNumericValue(quoteData.deliveryFees) || 0;
            const dealerInventoryTaxes =
              getNumericValue(quoteData.dealerInventoryTaxes) || 0;

            // Calculate total
            const total =
              finalSalesTaxSubTotal +
              titleFee +
              registrationFee +
              localHandlingFee +
              dealerServiceFeeAmount +
              deliveryFees +
              dealerInventoryTaxes +
              otherFees;

            return (
              <>
                {/* Taxes Section */}
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    Taxes:
                  </Typography>

                  {/* Taxable Value Formula Summary */}
                  <Box sx={{ mb: 1, pl: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Taxable value:</strong> (Base Price + Doc Fees
                          + Additions + Dealer Service Fee + Warranties +
                          Extended Warranties + Protection Packages + Theft
                          Protection + Insurance Packages - Trade In Amount -
                          Tax Credit)
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "11px",
                            fontFamily: "monospace",
                            color: "#666",
                          }}
                        >
                          ({formatFormulaValue(basePrice)} +{" "}
                          {formatFormulaValue(docFees)} +{" "}
                          {formatFormulaValue(addOns)} +{" "}
                          {formatFormulaValue(dealerServiceFee)} +{" "}
                          {formatFormulaValue(warrants)} +{" "}
                          {formatFormulaValue(extendedWarranties)} +{" "}
                          {formatFormulaValue(protectionPackages)} +{" "}
                          {formatFormulaValue(theftProtection)} +{" "}
                          {formatFormulaValue(insurancePackages)} -{" "}
                          {formatFormulaValue(tradeInValue, 1)} -{" "}
                          {formatFormulaValue(taxCreditAmount)})
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        {formatCurrency(taxableAmount.toString())}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Additionals Section */}
                  {(addOns > 0 ||
                    warrants > 0 ||
                    extendedWarranties > 0 ||
                    protectionPackages > 0 ||
                    theftProtection > 0 ||
                    insurancePackages > 0) && (
                    <Box sx={{ mb: 1, pl: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "bold", mb: 0.5 }}
                      >
                        Additionals:
                      </Typography>
                      <Box sx={{ pl: 2 }}>
                        {addOns > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              py: 0.25,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "11px" }}
                            >
                              Add On's:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "11px" }}
                            >
                              {formatCurrency(addOns.toString())}
                            </Typography>
                          </Box>
                        )}
                        {warrants > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              py: 0.25,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "11px" }}
                            >
                              Warranties:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "11px" }}
                            >
                              {formatCurrency(warrants.toString())}
                            </Typography>
                          </Box>
                        )}
                        {extendedWarranties > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              py: 0.25,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "11px" }}
                            >
                              Extended Warranties:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "11px" }}
                            >
                              {formatCurrency(extendedWarranties.toString())}
                            </Typography>
                          </Box>
                        )}
                        {protectionPackages > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              py: 0.25,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "11px" }}
                            >
                              Protection Packages:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "11px" }}
                            >
                              {formatCurrency(protectionPackages.toString())}
                            </Typography>
                          </Box>
                        )}
                        {theftProtection > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              py: 0.25,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "11px" }}
                            >
                              Theft Protection:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "11px" }}
                            >
                              {formatCurrency(theftProtection.toString())}
                            </Typography>
                          </Box>
                        )}
                        {insurancePackages > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              py: 0.25,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "11px" }}
                            >
                              Insurance Packages:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "11px" }}
                            >
                              {formatCurrency(insurancePackages.toString())}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* State Tax */}
                  {(finalStateTax !== 0 || stateTaxFee) && (
                    <Box sx={{ mb: 1, pl: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body2">
                          <strong>
                            State Tax{feeState ? ` - ${feeState}` : ""}
                            {stateTaxRate > 0
                              ? ` - ${(stateTaxRate * 100).toFixed(4)}%`
                              : ""}
                            :
                          </strong>
                        </Typography>
                        <Typography variant="body2">
                          {formatCurrency(finalStateTax.toString())}
                        </Typography>
                      </Box>
                      {stateTaxRate > 0 && taxableAmount !== 0 && (
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "11px",
                            fontFamily: "monospace",
                            color: "#666",
                            mb: 0.5,
                          }}
                        >
                          {stateTaxRate.toFixed(6)} * (
                          {formatFormulaValue(basePrice)} +{" "}
                          {formatFormulaValue(docFees)} +{" "}
                          {formatFormulaValue(addOns)} +{" "}
                          {formatFormulaValue(dealerServiceFee)} +{" "}
                          {formatFormulaValue(warrants)} +{" "}
                          {formatFormulaValue(extendedWarranties)} +{" "}
                          {formatFormulaValue(protectionPackages)} +{" "}
                          {formatFormulaValue(theftProtection)} +{" "}
                          {formatFormulaValue(insurancePackages)} -{" "}
                          {formatFormulaValue(tradeInValue, 1)} -{" "}
                          {formatFormulaValue(taxCreditAmount)})
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* County Tax */}
                  {(finalCountyTax !== 0 || countyTaxFee) && (
                    <Box sx={{ mb: 1, pl: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body2">
                          <strong>
                            County Tax{feeCounty ? ` - ${feeCounty}` : ""}
                            {countyTaxRate > 0
                              ? ` - ${(countyTaxRate * 100).toFixed(4)}%`
                              : ""}
                            :
                          </strong>
                        </Typography>
                        <Typography variant="body2">
                          {formatCurrency(finalCountyTax.toString())}
                        </Typography>
                      </Box>
                      {countyTaxRate > 0 && taxableAmount !== 0 && (
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "11px",
                            fontFamily: "monospace",
                            color: "#666",
                            mb: 0.5,
                          }}
                        >
                          {countyTaxRate.toFixed(6)} * (
                          {formatFormulaValue(basePrice)} +{" "}
                          {formatFormulaValue(docFees)} +{" "}
                          {formatFormulaValue(addOns)} +{" "}
                          {formatFormulaValue(dealerServiceFee)} +{" "}
                          {formatFormulaValue(warrants)} +{" "}
                          {formatFormulaValue(extendedWarranties)} +{" "}
                          {formatFormulaValue(protectionPackages)} +{" "}
                          {formatFormulaValue(theftProtection)} +{" "}
                          {formatFormulaValue(insurancePackages)} -{" "}
                          {formatFormulaValue(tradeInValue, 1)} -{" "}
                          {formatFormulaValue(taxCreditAmount)})
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Sales Tax Sub-Total */}
                  {finalSalesTaxSubTotal > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 0.5,
                        pl: 2,
                      }}
                    >
                      <Typography variant="body2">
                        <strong>Sales Tax Sub-Total:</strong>
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(finalSalesTaxSubTotal.toString())}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Fixed Fees and Expenses Section */}
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    Fixed Fees and Expenses:
                  </Typography>

                  {/* State Tax - Always show if we have state data */}
                  {feeState && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 0.5,
                        pl: 2,
                      }}
                    >
                      <Typography variant="body2">
                        <strong>
                          State Tax{feeState ? ` - ${feeState}` : ""}
                          {stateTaxRate > 0
                            ? ` - ${(stateTaxRate * 100).toFixed(4)}%`
                            : ""}
                          :
                        </strong>
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(finalStateTax.toString())}
                      </Typography>
                    </Box>
                  )}

                  {/* County Tax - Always show if we have county data */}
                  {feeCounty && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 0.5,
                        pl: 2,
                      }}
                    >
                      <Typography variant="body2">
                        <strong>
                          County Tax{feeCounty ? ` - ${feeCounty}` : ""}
                          {countyTaxRate > 0
                            ? ` - ${(countyTaxRate * 100).toFixed(4)}%`
                            : ""}
                          :
                        </strong>
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(finalCountyTax.toString())}
                      </Typography>
                    </Box>
                  )}

                  {localHandlingFee > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 0.5,
                        pl: 2,
                      }}
                    >
                      <Typography variant="body2">
                        <strong>Local Handling Fee:</strong>
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(localHandlingFee.toString())}
                      </Typography>
                    </Box>
                  )}

                  {titleFee > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 0.5,
                        pl: 2,
                      }}
                    >
                      <Typography variant="body2">
                        <strong>Title Fee:</strong>
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(titleFee.toString())}
                      </Typography>
                    </Box>
                  )}

                  {registrationFee > 0 && (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          py: 0.5,
                          pl: 2,
                        }}
                      >
                        <Typography variant="body2">
                          <strong>Plates:</strong>
                        </Typography>
                        <Typography variant="body2">
                          {formatCurrency(registrationFee.toString())}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          py: 0.5,
                          pl: 2,
                        }}
                      >
                        <Typography variant="body2">
                          <strong>Registration Fee:</strong>
                        </Typography>
                        <Typography variant="body2">
                          {formatCurrency(registrationFee.toString())}
                        </Typography>
                      </Box>
                    </>
                  )}

                  {otherFees > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 0.5,
                        pl: 2,
                      }}
                    >
                      <Typography variant="body2">
                        <strong>Other Fees:</strong>
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(otherFees.toString())}
                      </Typography>
                    </Box>
                  )}

                  {dealerServiceFeeAmount > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 0.5,
                        pl: 2,
                      }}
                    >
                      <Typography variant="body2">
                        <strong>Dealer Service Fee:</strong>
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(dealerServiceFeeAmount.toString())}
                      </Typography>
                    </Box>
                  )}

                  {deliveryFees > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 0.5,
                        pl: 2,
                      }}
                    >
                      <Typography variant="body2">
                        <strong>Delivery Fees:</strong>
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(deliveryFees.toString())}
                      </Typography>
                    </Box>
                  )}

                  {dealerInventoryTaxes > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 0.5,
                        pl: 2,
                      }}
                    >
                      <Typography variant="body2">
                        <strong>Dealer Inventory Taxes:</strong>
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(dealerInventoryTaxes.toString())}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Total Payable */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 1.5,
                    mt: 2,
                    borderTop: "2px solid #000",
                    borderBottom: "2px solid #000",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: "bold", fontSize: "16px" }}
                  >
                    <strong>Check Payable to TSF:</strong>
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: "bold", fontSize: "16px" }}
                  >
                    {formatCurrency(total.toString())}
                  </Typography>
                </Box>
              </>
            );
          })()}
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{ mb: 1, borderBottom: "1px solid #ddd", pb: 0.5 }}
        >
          Quote Information
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
            mb: 2,
          }}
        >
          <div>
            <strong>Driver Last Name:</strong>{" "}
            {formatValue(quoteData.driverLastName)}
          </div>
          <div>
            <strong>VIN Number:</strong> {formatValue(quoteData.vinNumber)}
          </div>
          <div>
            <strong>Service Form URL:</strong>{" "}
            {quoteData.serviceFormURL ? (
              <a
                href={quoteData.serviceFormURL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {quoteData.serviceFormURL}
              </a>
            ) : (
              "N/A"
            )}
          </div>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{ mb: 1, borderBottom: "1px solid #ddd", pb: 0.5 }}
        >
          Selection Info
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 2,
            mb: 2,
          }}
        >
          <div>
            <strong>State:</strong> {formatValue(quoteData.titleState)}
          </div>
          <div>
            <strong>County:</strong> {formatValue(quoteData.county)}
          </div>
          <div>
            <strong>City:</strong> {formatValue(quoteData.city)}
          </div>
          <div>
            <strong>Zip Code:</strong> {formatValue(quoteData.titleZip)}
          </div>
          <div>
            <strong>Transaction Type:</strong>{" "}
            {formatValue(quoteData.transactionType)}
          </div>
          <div>
            <strong>Outside City Limits:</strong>{" "}
            {formatValue(quoteData.outsideCityLimits)}
          </div>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{ mb: 1, borderBottom: "1px solid #ddd", pb: 0.5 }}
        >
          Vehicle Information
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 2,
            mb: 2,
          }}
        >
          <div>
            <strong>Year:</strong> {formatValue(quoteData.modelYear)}
          </div>
          <div>
            <strong>Make:</strong> {formatValue(quoteData.make)}
          </div>
          <div>
            <strong>Body Style:</strong> {formatValue(quoteData.bodyStyle)}
          </div>
          <div>
            <strong>Commercial Use:</strong>{" "}
            {formatValue(quoteData.commercialUse)}
          </div>
          <div>
            <strong>Odometer:</strong> {formatValue(quoteData.odometer)}
          </div>
          <div>
            <strong>Color:</strong> {formatValue(quoteData.color)}
          </div>
          <div>
            <strong>Cylinders:</strong> {formatValue(quoteData.cylindars)}
          </div>
          <div>
            <strong>Horsepower:</strong> {formatValue(quoteData.horsepower)}
          </div>
          <div>
            <strong>CC:</strong> {formatValue(quoteData.cc)}
          </div>
          <div>
            <strong>Rim Size:</strong> {formatValue(quoteData.rimSize)}
          </div>
          <div>
            <strong># of Tires:</strong> {formatValue(quoteData.numTires)}
          </div>
          <div>
            <strong># of Axles:</strong> {formatValue(quoteData.numAxles)}
          </div>
          <div>
            <strong>Weight:</strong> {formatValue(quoteData.weight)} lbs
          </div>
          <div>
            <strong>Registered Weight:</strong>{" "}
            {formatValue(quoteData.registeredWeight)} lbs
          </div>
          <div>
            <strong>GVW:</strong> {formatValue(quoteData.gvw)} lbs
          </div>
          <div>
            <strong>GVWR:</strong> {formatValue(quoteData.gvwr)} lbs
          </div>
          <div>
            <strong>Fuel Type:</strong> {formatValue(quoteData.fuelType)}
          </div>
          <div>
            <strong>MPG:</strong> {formatValue(quoteData.mpg)}
          </div>
          <div>
            <strong>Highway MPG:</strong> {formatValue(quoteData.highwayMpg)}
          </div>
          <div>
            <strong>Combined MPG:</strong> {formatValue(quoteData.combinedMpg)}
          </div>
          <div>
            <strong>Length:</strong> {formatValue(quoteData.length)}
          </div>
          <div>
            <strong>Square Footage:</strong>{" "}
            {formatValue(quoteData.squareFootage)}
          </div>
          <div>
            <strong>Registration Months:</strong>{" "}
            {formatValue(quoteData.registrationMonths)}
          </div>
          <div>
            <strong>Previous Registrations:</strong>{" "}
            {formatValue(quoteData.previousRegistrations)}
          </div>
          <div>
            <strong>EV Tax Credit:</strong> {formatValue(quoteData.evTaxCredit)}
          </div>
          {quoteData.evTaxCredit === "Apply credit to taxable amount" && (
            <div>
              <strong>Tax Credit Amount:</strong>{" "}
              {formatCurrency(quoteData.taxCreditAmount)}
            </div>
          )}
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{ mb: 1, borderBottom: "1px solid #ddd", pb: 0.5 }}
        >
          Transaction Information
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 2,
            mb: 2,
          }}
        >
          <div>
            <strong>Active Military:</strong>{" "}
            {formatValue(quoteData.activeMilitary)}
          </div>
          <div>
            <strong>MCO/Title:</strong> {formatValue(quoteData.mcoTitle)}
          </div>
          <div>
            <strong>Plate Transfer:</strong>{" "}
            {formatValue(quoteData.plateTransfer)}
          </div>
          <div>
            <strong>Plate Number:</strong> {formatValue(quoteData.plateNumber)}
          </div>
          <div>
            <strong>Plate Expiration:</strong>{" "}
            {formatValue(quoteData.plateExpiration)}
          </div>
          <div>
            <strong>Plate Type:</strong> {formatValue(quoteData.plateType)}
          </div>
          <div>
            <strong>Lien:</strong> {formatValue(quoteData.lien)}
          </div>
          <div>
            <strong>Trade In:</strong> {formatValue(quoteData.tradeIn)}
          </div>
          {quoteData.tradeIn && (
            <>
              <div>
                <strong>Trade In Vehicle Type:</strong>{" "}
                {formatValue(quoteData.tradeInVehicleType)}
              </div>
              <div>
                <strong>Trade In Vehicle Sub Type:</strong>{" "}
                {formatValue(quoteData.tradeInVehicleSubType)}
              </div>
              <div>
                <strong>Trade In Model Year:</strong>{" "}
                {formatValue(quoteData.tradeInModelYear)}
              </div>
            </>
          )}
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{ mb: 1, borderBottom: "1px solid #ddd", pb: 0.5 }}
        >
          Taxable Fees Information
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 2,
            mb: 2,
          }}
        >
          <div>
            <strong>Purchase Date:</strong>{" "}
            {formatValue(quoteData.purchaseDate)}
          </div>
          <div>
            <strong>MSRP:</strong> {formatCurrency(quoteData.msrp)}
          </div>
          <div>
            <strong>Base Price:</strong> {formatCurrency(quoteData.basePrice)}
          </div>
          <div>
            <strong>Trade In Value:</strong>{" "}
            {formatCurrency(quoteData.tradeInValue)}
          </div>
          <div>
            <strong>Dealer Rebate:</strong>{" "}
            {formatCurrency(quoteData.dealerRebate)}
          </div>
          <div>
            <strong>Manufacturer Rebate:</strong>{" "}
            {formatCurrency(quoteData.manufacturerRebate)}
          </div>
          <div>
            <strong>Discount:</strong> {formatCurrency(quoteData.discount)}
          </div>
          <div>
            <strong>Doc Fees:</strong> {formatCurrency(quoteData.docFees)}
          </div>
          <div>
            <strong>Warrants:</strong> {formatCurrency(quoteData.warrants)}
          </div>
          <div>
            <strong>Extended Warranties:</strong>{" "}
            {formatCurrency(quoteData.extendedWarranties)}
          </div>
          <div>
            <strong>Add On's:</strong> {formatCurrency(quoteData.addOns)}
          </div>
          <div>
            <strong>Protection Packages:</strong>{" "}
            {formatCurrency(quoteData.protectionPackages)}
          </div>
          <div>
            <strong>Theft Protection:</strong>{" "}
            {formatCurrency(quoteData.theftProtection)}
          </div>
          <div>
            <strong>Insurance Packages:</strong>{" "}
            {formatCurrency(quoteData.insurancePackages)}
          </div>
          <div>
            <strong>Dealer Inventory Taxes:</strong>{" "}
            {formatCurrency(quoteData.dealerInventoryTaxes)}
          </div>
          <div>
            <strong>Delivery Fees:</strong>{" "}
            {formatCurrency(quoteData.deliveryFees)}
          </div>
          <div>
            <strong>Taxes Paid to Dealer State:</strong>{" "}
            {formatCurrency(quoteData.taxesPaidDealerState)}
          </div>
          <div>
            <strong>Dealer Service Fee:</strong>{" "}
            {formatCurrency(quoteData.dealerServiceFee)}
          </div>
          <div>
            <strong>Cap Cost Reduction:</strong>{" "}
            {formatCurrency(quoteData.capCostReduction)}
          </div>
          <div>
            <strong>Lease Monthly Payment:</strong>{" "}
            {formatCurrency(quoteData.leaseMonthlyPayment)}
          </div>
          <div>
            <strong>Lease Number of Payments:</strong>{" "}
            {formatValue(quoteData.leaseNumberPayments)}
          </div>
        </Box>
      </Box>
    </Box>
  );
}
